'use client';

/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { Container, FlexItem, FlexLayout, Icon, Logo, ToggleSwitch } from '@wci-tech/core-ui';

import { useTranslations } from 'next-intl';

import { GoMoon } from 'react-icons/go';
import { MdOutlineLightMode } from 'react-icons/md';

import { BookADemoButton } from '@/components';
import { appConfig } from '@/config/appConfig';
import { useThemeMode } from '@/context';

import Navbar from '../navbar/Navbar';

import { useStyles } from './HeaderTopSection.styles';

type HeaderTopSectionProps = {
  scrolled?: boolean;
  showMobileMenu?: boolean;
  isHeaderMode?: boolean;
};

export const HeaderTopSection = ({
  scrolled = false,
  showMobileMenu = true,
}: HeaderTopSectionProps) => {
  const styles = useStyles(scrolled, showMobileMenu);

  const t = useTranslations('Header');

  const { isDark, toggleTheme } = useThemeMode();

  const handleToggleTheme = () => {
    toggleTheme(!isDark);
  };

  return (
    <div css={[styles.header, styles.topSection]}>
      <Container css={styles.container}>
        <FlexLayout
          justifyContent="space-between"
          alignItems="center"
          padding={{
            default: 0,
          }}
          direction={{
            default: 'row',
          }}
        >
          <FlexItem css={styles.desktopLogo}>
            <Logo
              src="/images/wciLogo.png"
              decorative={false}
              width={190}
              height={50}
              priority
              fetchPriority="high"
              href="/"
              alt={t('logoAlt')}
              linkAriaLabel={t('logoAlt')}
            />
          </FlexItem>

          <FlexItem css={styles.mobileLogo}>
            <Logo
              src="/images/monoLogo.png"
              decorative={false}
              width={58}
              height={50}
              href="/"
              alt={t('logoAlt')}
              linkAriaLabel={t('logoAlt')}
            />
          </FlexItem>

          <FlexItem>
            <FlexLayout
              alignItems="center"
              gap={24}
              padding={{
                default: 0,
              }}
            >
              <FlexItem css={styles.toggleContainer}>
                <ToggleSwitch
                  id="header-theme-toggle-switch"
                  aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                  onIcon={<Icon name={GoMoon} color="white" />}
                  offIcon={<Icon name={MdOutlineLightMode} color="black" />}
                  checked={isDark}
                  onChange={handleToggleTheme}
                  size={90}
                  customStyles={{
                    wrapper: styles.toggleWrapper,

                    track: styles.track,

                    thumb: css`
                      background-color: ${isDark ? 'black' : 'white'};
                    `,
                  }}
                />
              </FlexItem>

              <FlexItem css={styles.btnContainer}>
                <BookADemoButton
                  shape="pill"
                  variant="linear-gradient"
                  buttonText={t('bookDemo')}
                  customStyles={{
                    base: styles.headerBookADemo,
                  }}
                  calendlyUrl={appConfig.integrations.calendly.eventUrl}
                />
              </FlexItem>
            </FlexLayout>
          </FlexItem>

          <FlexItem css={styles.mobileControls}>
            <Navbar isHeaderMode showMobileNavigation={showMobileMenu} />
          </FlexItem>
        </FlexLayout>
      </Container>
    </div>
  );
};
